import {
  useEffect,
  useId,
  useRef,
  type FormEvent,
  type ReactNode,
} from "react";
import { cmsStrings } from "../terminology";

type Props = {
  title: string;
  description?: ReactNode;
  summary?: ReactNode;
  password: string;
  onPasswordChange: (value: string) => void;
  confirming: boolean;
  confirmLabel: string;
  /** primary = confirm; danger = destructive; critical = kill-switch / emergency only */
  tone?: "primary" | "danger" | "critical";
  onCancel: () => void;
  onConfirm: (e: FormEvent) => void;
};

export function PasswordConfirmModal({
  title,
  description,
  summary,
  password,
  onPasswordChange,
  confirming,
  confirmLabel,
  tone = "primary",
  onCancel,
  onConfirm,
}: Props) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    passwordRef.current?.focus();

    function focusables() {
      const root = dialogRef.current;
      if (!root) return [] as HTMLElement[];
      return Array.from(
        root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key !== "Tab") return;
      const list = focusables();
      if (list.length === 0) return;
      const first = list[0]!;
      const last = list[list.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [onCancel]);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onCancel}>
      <div
        ref={dialogRef}
        className={`modal modal--confirm modal--confirm-${tone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-vt-password-confirm
        data-tone={tone}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <h2 id={titleId}>{title}</h2>
          {description && <p className="modal__description">{description}</p>}
        </header>
        {summary && <div className="modal__summary">{summary}</div>}
        <form className="modal__form" onSubmit={onConfirm}>
          <label>
            <span>{cmsStrings.shell.passwordConfirmLabel}</span>
            <input
              ref={passwordRef}
              type="password"
              name="password"
              dir="ltr"
              className="ltr-isolate"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              required
              autoComplete="current-password"
              spellCheck={false}
            />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn--ghost" onClick={onCancel}>
              {cmsStrings.shell.cancel}
            </button>
            <button
              type="submit"
              className={`btn ${
                tone === "critical"
                  ? "btn--accent"
                  : tone === "danger"
                    ? "btn--danger"
                    : "btn--primary"
              }`}
              disabled={confirming}
              aria-busy={confirming}
            >
              {confirming ? cmsStrings.shell.loading : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
